import { ListStatus } from "@dashboard/devices/store";
import {
    cancelBuild as apiCancelBuild,
    createBuild as apiCreateBuild,
    createProduct as apiCreateProduct,
    deleteBuild as apiDeleteBuild,
    deleteProduct as apiDeleteProduct,
    deleteProductChangelog as apiDeleteProductChangelog,
    fetchFileDownloadLinks as apiFetchFileDownload,
    fetchProductBuilds as apiFetchProductBuilds,
    fetchProductChangeLogs as apiFetchProductChangeLogs,
    fetchProducts as apiFetchProducts,
    FetchProductsPayload,
    fetchProductsWithBuilds as apiFetchProductsWithBuilds,
    importProductBuild as apiImportProductBuild,
    setBuildChanges as apiSetBuildChanges,
    updateBuildChannel as apiUpdateBuildChannel,
    updateBuildMetadata as apiUpdateBuildMetadata,
    updateBuildRollout as apiUpdateBuildRollout,
    updateFullOtaCompatibility as apiUpdateFullOtaCompatibility,
    updateProduct as apiUpdateProduct,
} from "@dashboard/products/api";
import { createAsyncThunk, createEntityAdapter, createSlice, EntityState } from "@reduxjs/toolkit";

import { Build, Product } from "../types";

export const productsAdapter = createEntityAdapter<Product, number>({
    selectId: (product) => product.id,
});
export const buildsAdapter = createEntityAdapter<Build, number>({
    selectId: (build) => build.buildId,
    sortComparer: (a, b) => (new Date(b.created) > new Date(a.created) ? 1 : -1),
});

export const fetchProductsWithBuilds = createAsyncThunk("products/fetchProductsWithBuilds", apiFetchProductsWithBuilds);
export const fetchProducts = createAsyncThunk<Product[], FetchProductsPayload>("products/fetchProducts", apiFetchProducts);
export const fetchProductBuilds = createAsyncThunk("products/fetchProductBuilds", apiFetchProductBuilds);
export const fetchProductChangelog = createAsyncThunk("products/fetchProductChangelog", apiFetchProductChangeLogs);
export const setBuildChangelog = createAsyncThunk("products/setBuildChangelog", apiSetBuildChanges);
export const deleteProductChangelog = createAsyncThunk("products/deleteProductChangeLog", apiDeleteProductChangelog);
const refetchProducts = createAsyncThunk("products/refetchProducts", apiFetchProductsWithBuilds);
export const createProduct = createAsyncThunk("products/createProduct", apiCreateProduct);
export const updateProduct = createAsyncThunk("products/updateProduct", apiUpdateProduct);
export const deleteProduct = createAsyncThunk("products/deleteProduct", apiDeleteProduct);

export const createBuild = createAsyncThunk("products/createBuild", apiCreateBuild);
export const cancelBuild = createAsyncThunk("products/cancelBuild", apiCancelBuild);
export const deleteBuild = createAsyncThunk("products/deleteBuild", apiDeleteBuild);
export const updateBuildMetadata = createAsyncThunk("products/updateBuildMetadata", apiUpdateBuildMetadata);
export const updateBuildChannel = createAsyncThunk("products/updateBuildChannel", apiUpdateBuildChannel);
export const updateBuildRollout = createAsyncThunk("products/updateBuildRollout", apiUpdateBuildRollout);
export const updateFullOtaCompatibility = createAsyncThunk("products/updateFullOtaCompatibility", apiUpdateFullOtaCompatibility);

export const fetchFileDownloadLinks = createAsyncThunk("products/fetchFileDownload", apiFetchFileDownload);
export const importProductBuild = createAsyncThunk("products/importProductBuild", apiImportProductBuild);

type ProductState = {
    list: {
        products: {
            status: ListStatus | null;
            items: EntityState<Product, number>;
        };
        builds: {
            items: EntityState<Build, number>;
        };
    };
    error: null | unknown;
    useImport: boolean;
};

const initialState: ProductState = {
    list: {
        products: {
            status: null,
            items: productsAdapter.getInitialState(),
        },
        builds: {
            items: buildsAdapter.getInitialState(),
        },
    },
    error: null,
    useImport: false,
};

const slice = createSlice({
    name: "products",
    initialState,
    reducers: {
        setUseImport: (state, { payload = false }) => {
            state.useImport = payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProductsWithBuilds.pending, (state) => {
                state.list.products.status = "pending";
            })
            .addCase(fetchProductsWithBuilds.rejected, (state) => {
                state.list.products.status = "rejected";
            })
            .addCase(fetchProductsWithBuilds.fulfilled, (state, { payload }) => {
                state.list.products.status = "fulfilled";
                const { products, builds } = payload;
                productsAdapter.setAll(state.list.products.items, products);
                buildsAdapter.setAll(state.list.builds.items, builds);
            })
            .addCase(fetchProducts.pending, (state) => {
                state.list.products.status = "pending";
            })
            .addCase(fetchProducts.rejected, (state) => {
                state.list.products.status = "rejected";
            })
            .addCase(fetchProducts.fulfilled, (state, { payload }) => {
                state.list.products.status = "fulfilled";
                productsAdapter.setAll(state.list.products.items, payload);
            })
            .addCase(fetchProductBuilds.fulfilled, (state, { payload }) => {
                buildsAdapter.upsertMany(state.list.builds.items, payload);
            })
            .addCase(fetchProductChangelog.fulfilled, (state, { payload, meta }) => {
                const productId = meta.arg.id;
                const product = state.list.products.items.entities[productId];
                if (product) {
                    product.changes = payload;
                }
            })
            .addCase(setBuildChangelog.fulfilled, (state, { payload, meta }) => {
                const productId = meta.arg.productId;
                const product = state.list.products.items.entities[productId];
                if (product) {
                    product.changes = payload;
                }
            })
            .addCase(refetchProducts.fulfilled, (state, { payload }) => {
                state.list.products.status = "fulfilled";

                const { products, builds } = payload;
                productsAdapter.updateMany(
                    state.list.products.items,
                    products.map((product) => ({
                        id: product.id,
                        changes: product,
                    })),
                );
                buildsAdapter.updateMany(
                    state.list.builds.items,
                    builds.map((build) => ({
                        id: build.buildId,
                        changes: build,
                    })),
                );
            })
            .addCase(fetchFileDownloadLinks.fulfilled, (state, { meta, payload }) => {
                const buildId = meta.arg.buildId;

                buildsAdapter.updateOne(state.list.builds.items, {
                    id: buildId,
                    changes: {
                        artifacts: payload.artifacts,
                    },
                });
            })
            .addCase(createProduct.fulfilled, (state, { payload }) => {
                const product = payload;
                productsAdapter.addOne(state.list.products.items, product);
            })
            .addCase(updateProduct.pending, (state) => {
                state.list.products.status = "updating";
            })
            .addCase(updateProduct.fulfilled, (state, { payload }) => {
                state.list.products.status = "fulfilled";
                const product = payload;
                productsAdapter.updateOne(state.list.products.items, {
                    id: product.id,
                    changes: {
                        ...product,
                    },
                });
            })
            .addCase(deleteProductChangelog.fulfilled, (state, { payload, meta }) => {
                const { productId } = meta.arg;
                const product = state.list.products.items.entities[productId];

                if (product) {
                    product.changes = payload;
                }
            })
            .addCase(deleteProduct.fulfilled, (state, { meta }) => {
                const id = meta.arg;
                const product = productsAdapter
                    .getSelectors()
                    .selectAll(state.list.products.items)
                    ?.find((_product) => _product.id === id);

                if (product) {
                    productsAdapter.removeOne(state.list.products.items, product.id);
                }
            })
            .addCase(createBuild.fulfilled, (state, { payload, meta }) => {
                const build = payload;

                const productId = build?.productId ?? meta.arg.productId;
                const existingProduct = productId ? state.list.products.items.entities[productId] : null;
                if (existingProduct && meta.arg.config?.changes) {
                    productsAdapter.updateOne(state.list.products.items, {
                        id: productId,
                        changes: {
                            changes: {
                                ...existingProduct?.changes,
                                [build.version]: meta.arg.config.changes,
                            },
                        },
                    });
                }

                buildsAdapter.addOne(state.list.builds.items, {
                    ...build,
                    buildStatus: build.buildStatus ?? "pending",
                });
            })
            .addCase(cancelBuild.fulfilled, (state, { payload }) => {
                const buildId = payload;
                buildsAdapter.updateOne(state.list.builds.items, {
                    id: buildId,
                    changes: {
                        buildStatus: "canceled",
                    },
                });
            })
            .addCase(deleteBuild.fulfilled, (state, { payload }) => {
                buildsAdapter.removeOne(state.list.builds.items, payload);
            })
            .addCase(updateBuildRollout.fulfilled, (state, { payload }) => {
                buildsAdapter.updateOne(state.list.builds.items, {
                    id: payload.buildId,
                    changes: {
                        rolloutPercentage: payload.rolloutPercentage,
                    },
                });
            })
            .addCase(updateBuildMetadata.fulfilled, (state, { payload }) => {
                const build = buildsAdapter
                    .getSelectors()
                    .selectAll(state.list.builds.items)
                    .find((s) => s.buildId === payload.buildId);

                buildsAdapter.updateOne(state.list.builds.items, {
                    id: payload.buildId,
                    changes: {
                        metadata: {
                            ...build?.metadata,
                            user: payload.metadata,
                        },
                    },
                });
            })
            .addCase(updateBuildChannel.fulfilled, (state, { payload }) => {
                buildsAdapter.updateOne(state.list.builds.items, {
                    id: payload.buildId,
                    changes: {
                        channel: payload.channel,
                    },
                });
            })
            .addCase(updateFullOtaCompatibility.fulfilled, (state, { payload }) => {
                const { fileId, compatibility } = payload;
                const build = buildsAdapter
                    .getSelectors()
                    .selectAll(state.list.builds.items)
                    .find((b) => b.artifacts.some((artifact) => artifact.id === fileId));

                if (!build) {
                    return;
                }

                buildsAdapter.updateOne(state.list.builds.items, {
                    id: build.buildId,
                    changes: {
                        artifacts: build.artifacts.map((artifact) =>
                            artifact.id === fileId ? { ...artifact, compatibility } : artifact,
                        ),
                    },
                });
            })
            .addCase(importProductBuild.fulfilled, (state, { payload, meta }) => {
                const { product, build } = payload;
                productsAdapter.updateOne(state.list.products.items, {
                    id: meta.arg.productId,
                    changes: product,
                });
                buildsAdapter.addOne(state.list.builds.items, build);
            });
    },
});

export default slice.reducer;
